/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useRef } from 'react';

/**
 * Observes a sentinel element and triggers onLoadMore when scrolled into view.
 * Handles both window scrolling and inner layout <main> container scrolling.
 */
export default function useInfiniteScroll({ onLoadMore, hasMore, disabled }) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (disabled || !hasMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const scrollContainer = sentinel.closest('main') || null;

    // 1. Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !disabled && hasMore) {
          onLoadMoreRef.current();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '250px',
      }
    );
    observer.observe(sentinel);

    // 2. Scroll event listener fallback on the actual scrolling container
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (disabled || !hasMore) return;
        const target = scrollContainer || document.documentElement;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom < 300) {
          onLoadMoreRef.current();
        }
      });
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    // 3. Immediate check if container is not tall enough to scroll
    const checkImmediate = setTimeout(() => {
      if (!disabled && hasMore && sentinelRef.current) {
        const rect = sentinelRef.current.getBoundingClientRect();
        const viewportHeight = scrollContainer ? scrollContainer.clientHeight : window.innerHeight;
        if (rect.top <= viewportHeight + 100) {
          onLoadMoreRef.current();
        }
      }
    }, 100);

    return () => {
      clearTimeout(checkImmediate);
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasMore, disabled]);

  return sentinelRef;
}
